/* ────────────────────────────────────────────────────────────────────────────
 * Implant review stickers — Ram's three steps, 2026-09-17:
 *   1 คัดกรอง   is this patient suitable to call about an implant?   👍 / 👎
 *   2 ตรวจซ้ำ   a second look at step 1's 👍                          👍 / 👎
 *   3 โทร       the call itself, exactly as before (recall notes)
 * "Everyone can do the step 1-2-3" — no roles, no dentist, no expiry.
 *
 * Stored one row per click in Xano table implant_review (110): {step, result:
 * pass|fail|clear}. The latest row per (patient, step) is the answer; `clear` is the
 * undo. Step 2 only counts when it is NEWER than the latest step 1, so re-screening a
 * patient sends them back to รอตรวจซ้ำ.
 *
 * 🔑 THE SAME RULE LIVES IN THREE PLACES and they must agree:
 *   api 650 report/implant_gap          `rv` / `rs` CTEs  (the list column + filters)
 *   api 660 report/implant_review_outcome `ready` CTE     (ผลหลังรีวิวผ่าน)
 *   reviewState() below                                   (the popup)
 * src/lib/implantReview.test.mjs transcribes the SQL and checks this against it on
 * every sequence of up to four clicks.
 *
 * A review is NOT a recall note, on purpose: a recall insert fires trigger 7 and
 * resets patient.last_recall, which would drop the patient out of list B and under
 * "ซ่อนคนที่เพิ่งโทรไป" before anyone had called.
 * ──────────────────────────────────────────────────────────────────────────── */

// `code` is the api 650 review_status input: ARRAY['none','wait','ready','fail'][code].
// Reordering this array changes what every filter chip asks for.
export const REVIEW_STATUSES = [
  { id: 'none', code: 1, label: 'ยังไม่รีวิว', hint: 'ยังไม่มีใครคัดกรอง', pill: 'bg-slate-50 text-slate-500 ring-slate-200', dot: 'bg-slate-300' },
  { id: 'wait', code: 2, label: 'รอตรวจซ้ำ', hint: 'ผ่านคัดกรองแล้ว รอคนตรวจซ้ำ', pill: 'bg-amber-50 text-amber-700 ring-amber-200', dot: 'bg-amber-400' },
  { id: 'ready', code: 3, label: 'พร้อมโทร', hint: 'ผ่านทั้ง 2 ขั้น — โทรได้', pill: 'bg-emerald-50 text-emerald-700 ring-emerald-200', dot: 'bg-emerald-500' },
  { id: 'fail', code: 4, label: 'ไม่ผ่าน', hint: 'ไม่ผ่านการรีวิว — ไม่ต้องโทรเสนอรากเทียม', pill: 'bg-rose-50 text-rose-700 ring-rose-200', dot: 'bg-rose-400' },
]
export const REVIEW_BY_ID = Object.fromEntries(REVIEW_STATUSES.map((s) => [s.id, s]))
export const reviewMeta = (id) => REVIEW_BY_ID[id] || REVIEW_BY_ID.none
/** '' / 'all' / unknown → 0 (no filter); otherwise the api 650 code. */
export const reviewStatusCode = (id) => REVIEW_BY_ID[id]?.code ?? 0

export const STEP_LABEL = { 1: 'คัดกรอง', 2: 'ตรวจซ้ำ' }
// note / reopen (2026-10-29) are step 0 — a reviewer's note and "ดึงกลับมาโทรตอนนี้".
// They never change a status: every reader counts steps 1 and 2 only.
export const RESULT_LABEL = { pass: 'ผ่าน', fail: 'ไม่ผ่าน', clear: 'ล้าง', note: 'บันทึก', reopen: 'ดึงกลับมาโทร' }

/** A round's start ('YYYY-MM-DD', api 678 round_start) → epoch ms at 00:00 Bangkok. */
export function roundStartMs(iso) {
  if (!iso || !/^\d{4}-\d{2}-\d{2}/.test(String(iso))) return 0
  const t = Date.parse(`${String(iso).slice(0, 10)}T00:00:00+07:00`)
  return Number.isFinite(t) && t > Date.parse('1901-01-01T00:00:00Z') ? t : 0
}

// What happened after a READY patient was approved (api 650 call_outcome, api 660).
// `code` is api 660's outcome input: ARRAY['not_called','called','success'][code].
// Since 2026-10-29 (implant/report.md §6g) a call is a recall note WITH an outcome
// chip, and นัดได้ is an implant-related appointment created within 14 days after a
// call (a BK chip also confirms one booked up to 14 days before it).
export const CALL_OUTCOMES = [
  { id: 'not_called', code: 1, label: 'ยังไม่โทร', pill: 'bg-slate-50 text-slate-500 ring-slate-200' },
  { id: 'called', code: 2, label: 'โทรแล้ว ยังไม่นัด', pill: 'bg-amber-50 text-amber-700 ring-amber-200' },
  { id: 'success', code: 3, label: 'นัดได้', pill: 'bg-emerald-50 text-emerald-700 ring-emerald-200' },
]
// Not a call outcome — a patient can be paid with any of the three — so it filters
// across them: api 660 outcome=4 is "paid", a closing bill after approval.
export const PAID_FILTER = { id: 'paid', code: 4, label: 'จ่ายแล้ว', pill: 'bg-emerald-600 text-white ring-emerald-600' }
export const OUTCOME_BY_ID = Object.fromEntries([...CALL_OUTCOMES, PAID_FILTER].map((o) => [o.id, o]))

/* "รีวิวล่าสุด" select. One value, two api 650 inputs: in<N> → reviewed_within=N,
 * out<N> → not_reviewed_within=N (which includes never reviewed). */
export const REVIEWED_OPTS = [
  { v: '', label: 'ไม่กรอง' },
  { v: 'in7', label: 'รีวิวภายใน 7 วัน' },
  { v: 'in14', label: 'รีวิวภายใน 14 วัน' },
  { v: 'in30', label: 'รีวิวภายใน 30 วัน' },
  { v: 'in60', label: 'รีวิวภายใน 60 วัน' },
  { v: 'in90', label: 'รีวิวภายใน 90 วัน' },
  { v: 'out30', label: 'ไม่ได้รีวิวเกิน 30 วัน / ยังไม่เคย' },
  { v: 'out60', label: 'ไม่ได้รีวิวเกิน 60 วัน / ยังไม่เคย' },
  { v: 'out90', label: 'ไม่ได้รีวิวเกิน 90 วัน / ยังไม่เคย' },
]
export function reviewedParams(v) {
  const m = /^(in|out)(\d+)$/.exec(String(v || ''))
  if (!m || !REVIEWED_OPTS.some((o) => o.v === v)) return { reviewed_within: 0, not_reviewed_within: 0 }
  const n = Number(m[2])
  return m[1] === 'in' ? { reviewed_within: n, not_reviewed_within: 0 } : { reviewed_within: 0, not_reviewed_within: n }
}

const isAnswer = (r) => r?.result === 'pass' || r?.result === 'fail'
const idOf = (r) => Number(r?.id) || 0

/**
 * One patient's review rows (any order, as GET /api/implant/review returns them)
 * → what api 650 would say about that patient.
 *   status        none | wait | ready | fail
 *   step1 / step2 the row that is the current answer for that step, or null
 *   staleStep2    a step-2 answer that no longer counts because step 1 was redone
 *   lastReviewAt  api 650 last_review_at (ms), or null
 *   canStep2      whether POST step 2 pass/fail would be accepted
 * `sinceMs` (2026-10-29): reviews belong to a ROUND — api 678 counts only rows written
 * on or after the round's start; pass roundStartMs(row.round_start) to match it. 0 =
 * every row (api 650 / api 660 and the popup outside tab 1).
 */
export function reviewState(rows, { sinceMs = 0 } = {}) {
  const list = (Array.isArray(rows) ? rows : [])
    .filter((r) => !sinceMs || (Number(r?.created_at) || 0) >= sinceMs)
  const latest = (step) => list
    .filter((r) => Number(r?.step) === step)
    .reduce((best, r) => (best == null || idOf(r) > idOf(best) ? r : best), null)
  const s1 = latest(1)
  const s2 = latest(2)
  const s2After = !!(s1 && s2 && idOf(s2) > idOf(s1))

  let status
  if (!s1) status = 'none'
  else if (s1.result === 'fail') status = 'fail'
  else if (s1.result !== 'pass') status = 'none'
  else if (s2After && s2.result === 'fail') status = 'fail'
  else if (s2After && s2.result === 'pass') status = 'ready'
  else status = 'wait'

  const step1 = isAnswer(s1) ? s1 : null
  const step2 = s1?.result === 'pass' && s2After && isAnswer(s2) ? s2 : null
  const staleStep2 = s1?.result === 'pass' && s2 && !s2After && isAnswer(s2) ? s2 : null
  const lastReviewAt = !step1 ? null : Number((step2 || step1).created_at) || null

  return { status, step1, step2, staleStep2, lastReviewAt, canStep2: s1?.result === 'pass' }
}

/**
 * The newest review id among a patient's rows (0 = none). Sent with every POST as
 * `expected_last_id`: api 658 refuses the click if the table already holds a newer one,
 * because reviews broadcast nothing and a popup opened before a colleague's review would
 * otherwise re-send an old answer — a repeated step-1 👍 undoes a พร้อมโทร, a repeated
 * step-2 👍 moves approval past a counted call and turns สำเร็จ back into ยังไม่โทร.
 */
export const lastReviewId = (rows) =>
  (Array.isArray(rows) ? rows : []).reduce((m, r) => Math.max(m, Number(r?.id) || 0), 0)

/** Success rate for the ผลหลังรีวิวผ่าน card: of the approved patients who were CALLED. */
export function successRate({ called = 0, success = 0 } = {}) {
  const c = (Number(called) || 0) + (Number(success) || 0)
  return c > 0 ? Math.round(((Number(success) || 0) / c) * 100) : null
}
